import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Col, Form, GetProp, Input, Row, Upload, UploadProps } from 'antd';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { FormProps } from 'antd/es/form/Form';

// services
import NotificationService from 'core/services/notification';
import { onCreateCountry } from 'modules/countries/services/countries';

// components
import BackButton from 'components/backButton/backButton';
import { useTranslation } from 'react-i18next';

type FieldType = {
	'name[en]': string;
	'name[ar]': string;
	code: string;
	flag: any;
};

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

function CreateCountryPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [createForm] = Form.useForm();
	const [imageUrl, setImageUrl] = useState<string>();

	const inputStyles = {
		width: 500,
		maxWidth: '100%',
	};

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const createCountryPayload = new FormData();
			createCountryPayload.append('name[ar]', values['name[ar]']);
			createCountryPayload.append('name[en]', values['name[en]']);
			createCountryPayload.append('code', values.code);
			createCountryPayload.append('active', '1');
			createCountryPayload.append('flag', values.flag);

			await onCreateCountry(createCountryPayload);
			NotificationService.success(t('COMMON.SUCCESS'), t('COUNTRIES.CREATED_SUCCESS'));
			navigate('/countries');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.OPS'), t('COUNTRIES.CREATE_ERROR'));
		}
	};

	const beforeUpload = (file: FileType) => {
		const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
		if (!isJpgOrPng) {
			NotificationService.error(t('COMMON.ONLY_PNG_JPG'));
			return false;
		}

		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(setImageUrl(reader.result as string));
		});

		return false;
	};

	const handleChange: UploadProps['onChange'] = (info) => {
		createForm.setFieldsValue({ flag: info.file });
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('COUNTRIES.CREATE_TITLE')}</h3>
			</div>

			<div className="sectionContainer">
				<h5 style={{ marginBottom: 20 }}>{t('COUNTRIES.DETAILS')}</h5>
				{/* <h5 style={{ marginBottom: 20 }}>Send invites</h5> */}

				<Form name="createForm" form={createForm} onFinish={onSubmit}>
					<Row gutter={[20, 20]}>
						<Col span={24} md={12}>
							<h6>{t('COUNTRIES.INFORMATION')}</h6>
							<Form.Item<FieldType> name="name[en]" rules={[{ required: true, message: t('COUNTRIES.VALIDATION.NAME_EN') }]}>
								<Input size="large" placeholder={t('COUNTRIES.PLACEHOLDER.NAME_EN')} style={inputStyles} />
							</Form.Item>

							<Form.Item<FieldType> name="name[ar]" rules={[{ required: true, message: t('COUNTRIES.VALIDATION.NAME_AR') }]}>
								<Input size="large" placeholder={t('COUNTRIES.PLACEHOLDER.NAME_AR')} style={inputStyles} />
							</Form.Item>

							<Form.Item<FieldType> name="code" rules={[{ required: true, message: t('COUNTRIES.VALIDATION.CODE') }]}>
								<Input size="large" placeholder={t('COUNTRIES.PLACEHOLDER.CODE')} style={inputStyles} />
							</Form.Item>
						</Col>

						<Col span={24} md={12}>
							<div>
								<h6>{t('COUNTRIES.FLAG')}</h6>
								<Form.Item<FieldType> name="flag" hidden>
									<Input />
								</Form.Item>
								<Upload
									className="squareInput"
									listType="picture-card"
									showUploadList={false}
									beforeUpload={beforeUpload}
									onChange={handleChange}>
									{imageUrl ? (
										<img src={imageUrl} alt="avatar" style={{ width: '100%' }} />
									) : (
										<button style={{ border: 0, background: 'none' }} type="button">
											{isSubmitting ? <LoadingOutlined /> : <PlusOutlined />}
											<div style={{ marginTop: 8 }}>{t('COMMON.UPLOAD')}</div>
										</button>
									)}
								</Upload>
							</div>
						</Col>
					</Row>
				</Form>

				<Form.Item>
					<Button
						size="middle"
						type="primary"
						htmlType="submit"
						form="createForm"
						loading={isSubmitting}
						style={{ marginTop: 20, display: 'flex' }}>
						{t('COMMON.ADD')}
					</Button>
				</Form.Item>
			</div>
		</>
	);
}

export default CreateCountryPage;
